import { useEffect, useRef, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/frontend-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const DEBOUNCE_MS = 600;

type CheckResult =
  | { status: 'FORGED'; freshness: string }
  | { status: 'NOT_FORGED' | 'INVALID' | 'ERROR'; reason: string };

const STATUS_COLOR: Record<CheckResult['status'], string> = {
  FORGED: '#2e7d32',
  NOT_FORGED: '#c62828',
  INVALID: '#c62828',
  ERROR: '#ed6c02',
};

// Explicit, because the portal theme can reset anchor styling and leave a link
// looking like plain text.
const LINK_COLOR = '#1565c0';
const HTTPS_URL_RE =/(https:\/\/[^\s]+)/g;
const TRAILING_PUNCTUATION_RE = /[.,;:)]+$/;

/**
 * Renders a reason string with any `https://` URL as a clickable link, so
 * the "what certification requires" pointer in the backend's message can be
 * followed. Everything else stays plain text (React escapes it); only
 * `https` URLs are linked.
 */
function linkifyReason(reason: string) {
  return reason.split(HTTPS_URL_RE).map((part, index) => {
    if (index % 2 === 0) return part;
    const url = part.replace(TRAILING_PUNCTUATION_RE, '');
    const trailing = part.slice(url.length);
    return (
      <span key={index}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: LINK_COLOR, textDecoration: 'underline' }}
        >
          {url}
        </a>
        {trailing}
      </span>
    );
  });
}

const NEXT_BUTTON_DISABLED_TITLE =
  'This project must come back FORGED before you can continue.';

/**
 * Finds the Stepper's Next/submit button for the step this field is
 * rendered on (Backstage's scaffolder Stepper renders exactly one
 * `<button type="submit">` per active step — see
 * @backstage/plugin-scaffolder-react's Stepper.esm.js) and toggles its
 * disabled state directly via the DOM.
 *
 * This is a deliberate, version-fragile workaround: the Stepper component
 * does not expose any prop, extension point, or formContext hook for a
 * field to gate step navigation (confirmed by reading the installed
 * @backstage/plugin-scaffolder-react@1.20.0 source — the button's
 * `disabled` prop is hardwired to the Stepper's own `isValidating` state
 * only). Setting `.disabled` on the DOM node survives React's re-renders
 * because React only touches an already-committed DOM attribute when the
 * prop driving it changes between renders, and `isValidating` does not
 * change on every keystroke. It also directly manipulates MUI's
 * `Mui-disabled` class so the button actually looks disabled, since MUI
 * applies that class from its own `disabled` prop, not from the native
 * DOM attribute. Re-verify this still works after any
 * @backstage/plugin-scaffolder-react upgrade.
 */
function setNextButtonDisabled(container: HTMLElement | null, disabled: boolean): void {
  const button = container?.closest('form')?.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!button) return;
  button.disabled = disabled;
  button.classList.toggle('Mui-disabled', disabled);
  button.style.pointerEvents = disabled ? 'none' : '';
  if (disabled) {
    button.title = NEXT_BUTTON_DISABLED_TITLE;
  } else {
    button.removeAttribute('title');
  }
}

/**
 * Live FORGED / NOT FORGED / INVALID readout for the "Deploy to Pivotree
 * Launchpad" template's Forge Certification step. Reads
 * formContext.formData.repo (step 1's answer for the repo slug — there is
 * no other way for a parameters-section field to see a sibling parameter,
 * per template.yaml's own comment) and calls the forge-certification-status
 * backend endpoint, which runs the exact same checkForgeCertification
 * (packages/backend/src/lib/forgeCertification.ts) the enforcing
 * pvt:forge:verify-certification action uses.
 *
 * This is a courtesy — faster feedback than waiting until Review/Create. It
 * is NOT the enforcement point: a submission that bypasses this page (a
 * direct scaffolder API call) skips this check entirely, which is exactly
 * why the verify-forge-certification step still runs server-side before
 * deploy.yaml is ever written. Never remove that step because this field
 * exists.
 *
 * It also visually disables the step's Next button (see
 * setNextButtonDisabled above) while the result is anything other than
 * FORGED, so a developer never sees a clickable Next that just bounces
 * them back with an error — same enforcement, better affordance.
 */
export const ForgeCertificationStatusField = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formContext } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const typedFormContext = formContext as { formData?: { repo?: string } } | undefined;
  const repo = typedFormContext?.formData?.repo?.trim() ?? '';
  const [state, setState] = useState<{ loading: boolean; result: CheckResult | null; error?: string }>({
    loading: false,
    result: null,
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const lastCheckedRepo = useRef<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const isForged = state.result?.status === 'FORGED';

  useEffect(() => {
    setNextButtonDisabled(containerRef.current, !isForged);
    return () => setNextButtonDisabled(containerRef.current, false);
  }, [isForged]);

  useEffect(() => {
    clearTimeout(debounceRef.current);

    if (!repo) {
      lastCheckedRepo.current = '';
      setState({ loading: false, result: null });
      onChange(undefined);
      return () => {};
    }

    debounceRef.current = setTimeout(() => {
      lastCheckedRepo.current = repo;
      setState({ loading: true, result: null });

      (async () => {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const res = await fetchApi.fetch(
          `${baseUrl}/forge-certification-status?workspace=pivotree&repo=${encodeURIComponent(repo)}`,
        );
        if (!res.ok) {
          throw new Error(`Certification check failed (HTTP ${res.status})`);
        }
        return (await res.json()) as CheckResult;
      })()
        .then(result => {
          // A newer repo value may have started its own check while this one
          // was in flight; don't let a stale response overwrite it.
          if (lastCheckedRepo.current !== repo) return;
          setState({ loading: false, result });
          onChange(result.status === 'FORGED' ? 'FORGED' : undefined);
        })
        .catch((err: Error) => {
          if (lastCheckedRepo.current !== repo) return;
          setState({ loading: false, result: null, error: err.message });
          onChange(undefined);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo]);

  let content;
  if (!repo) {
    content = (
      <Typography variant="body2" color="textSecondary">
        Enter a repository above to check its Forge certification status.
      </Typography>
    );
  } else if (state.loading) {
    content = (
      <Typography variant="body2" color="textSecondary">
        <CircularProgress size={14} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Checking Forge certification for {repo}…
      </Typography>
    );
  } else if (state.error) {
    content = (
      <Typography variant="body2" style={{ color: STATUS_COLOR.ERROR }}>
        Could not check certification status: {state.error}
      </Typography>
    );
  } else if (state.result) {
    const { result } = state;
    const label = result.status === 'FORGED' ? `FORGED (freshness: ${result.freshness})` : result.status.replace('_', ' ');
    const detail = result.status === 'FORGED' ? undefined : result.reason;
    content = (
      <>
        <Typography variant="body2" style={{ color: STATUS_COLOR[result.status], fontWeight: 600 }}>
          {label}
        </Typography>
        {detail && (
          <Typography variant="body2" color="textSecondary">
            {linkifyReason(detail)}
          </Typography>
        )}
      </>
    );
  }

  return <div ref={containerRef}>{content}</div>;
};
